import { Box, TextField, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { borderRadius, palette, spaces } from '../../../../assets/styles/themes/tokens';
import { ACTIVE_ACCOUNT_MESSAGE, EMAIL_MAX_LENGTH } from '../checkout.constants';
import type { EmailCheckState } from '../checkout.types';

const EmailInputSection: React.FC<{
	email: string;
	onEmailChange: (value: string) => void;
	state: EmailCheckState;
	/**
	 * SUB-73 — kimUno rejected the session create for this email with `409
	 * SubscriptionAlreadyActive`. Renders identically to the gate's own
	 * `has-active-sub` answer: same finding, same remedy, so same message.
	 */
	alreadyActive?: boolean;
}> = ({ email, onEmailChange, state, alreadyActive = false }) => {
	const { t } = useTranslation();

	const hasActiveSubscription = state.kind === 'has-active-sub' || alreadyActive;

	const errorMessage = hasActiveSubscription
		? t(ACTIVE_ACCOUNT_MESSAGE)
		: state.kind === 'invalid-format'
			? t('Please enter a valid email address')
			: state.kind === 'verify-failed'
				? t('Could not verify this email — please try again')
				: '';

	const isError =
		hasActiveSubscription || state.kind === 'invalid-format' || state.kind === 'verify-failed';

	return (
		<Box
			data-testid="stripe-checkout-email-section"
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: spaces.xs,
				marginBottom: spaces.lg,
			}}
		>
			<Typography variant="body1" color={palette.text.primary}>
				{t('E-Mail-Address')}
			</Typography>
			<TextField
				variant="outlined"
				placeholder={t('eg max.mustermann@beispiel.de')}
				type="email"
				size="small"
				value={email}
				onChange={(event) => onEmailChange(event.target.value)}
				fullWidth
				autoComplete="email"
				sx={{
					'& .MuiOutlinedInput-root': {
						borderRadius: borderRadius.xs,
					},
				}}
				inputProps={{ maxLength: EMAIL_MAX_LENGTH, 'data-testid': 'stripe-checkout-email-input' }}
				error={isError}
				helperText={errorMessage}
			/>
		</Box>
	);
};

export default EmailInputSection;
